import React, { useMemo, forwardRef, useCallback, useState } from 'react'
import FroalaEditorComponent from 'react-froala-wysiwyg'
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView'
import FroalaEditor from 'froala-editor'
import {
  FROALA_KEY,
  SURVEY_QUESTION_VIDEO_MAX_BYTES,
  SURVEY_QUESTION_VIDEO_MAX_SIZE_ERROR,
} from 'utils/constants'
import { countWords } from 'utils/misc'
import 'froala-editor/css/froala_style.min.css'
import 'froala-editor/css/froala_editor.pkgd.min.css'
import 'froala-editor/js/plugins.pkgd.min.js'
import 'styles/d3/froalaTextEditor.scss'

/**
 * Froala occasionally emits/returns HTML that is syntactically valid enough for the browser,
 * but semantically problematic for our mobile renderer:
 * - `<br>` nodes as direct children of `<ul>/<ol>` cause extra bullets/numbers
 * - center-aligned blocks can "leak" to subsequent text if content stays inside the same centered wrapper
 *
 * We normalize these cases before persisting to JSON.
 */
const normalizeFroalaHtml = (inputHtml, opts = {}) => {
  if (typeof inputHtml !== 'string') return inputHtml
  if (!inputHtml.includes('<')) return inputHtml
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return inputHtml

  const { fixListBreaks = true, fixCenterLeak = true } = opts
  const mayHaveListBrIssue = fixListBreaks && /<(ul|ol)\b/i.test(inputHtml) && /<br\b/i.test(inputHtml)
  const mayHaveCenterLeakIssue =
    fixCenterLeak && /text-align\s*:\s*center/i.test(inputHtml) && /<br\b/i.test(inputHtml)
  if (!mayHaveListBrIssue && !mayHaveCenterLeakIssue) return inputHtml

  try {
    const parser = new window.DOMParser()
    const doc = parser.parseFromString('<!doctype html><html><body></body></html>', 'text/html')
    doc.body.innerHTML = inputHtml

    // Issue 2: remove `<br>` nodes directly inside `<ul>/<ol>`
    if (mayHaveListBrIssue) {
      doc.body.querySelectorAll('ul, ol').forEach(listEl => {
        Array.from(listEl.childNodes).forEach(node => {
          if (node.nodeType === 1 && node.nodeName === 'BR') {
            node.remove()
          }
        })
      })
    }

    // Issue 1: prevent center alignment from affecting subsequent text.
    // If a centered div contains a "paragraph break" (two <br> in a row) and has content after,
    // we split it: keep the first part centered and move the remainder out after the div.
    const isWhitespaceTextNode = node => node?.nodeType === 3 && !node.nodeValue?.trim()
    const isBr = node => node?.nodeType === 1 && node.nodeName === 'BR'
    const hasCenterAlignStyle = el => {
      const style = (el.getAttribute('style') || '').replace(/\s+/g, '').toLowerCase()
      return style.includes('text-align:center')
    }

    if (mayHaveCenterLeakIssue) {
      doc.body.querySelectorAll('div[style]').forEach(div => {
        if (!hasCenterAlignStyle(div)) return

        const nodes = Array.from(div.childNodes)

        // Find two consecutive BRs, allowing whitespace text nodes between them
        let firstBrIdx = -1
        let secondBrIdx = -1
        for (let i = 0; i < nodes.length; i++) {
          if (!isBr(nodes[i])) continue
          // locate next non-whitespace node
          let j = i + 1
          while (j < nodes.length && isWhitespaceTextNode(nodes[j])) j++
          if (j < nodes.length && isBr(nodes[j])) {
            firstBrIdx = i
            secondBrIdx = j
            break
          }
        }

        if (firstBrIdx === -1) return

        // Check if there is any real content after the second BR
        let k = secondBrIdx + 1
        while (k < nodes.length && isWhitespaceTextNode(nodes[k])) k++
        if (k >= nodes.length) return

        const remainderFragment = doc.createDocumentFragment()
        for (let idx = k; idx < nodes.length; idx++) {
          remainderFragment.appendChild(nodes[idx])
        }

        // Remove from first BR onwards inside the centered div
        for (let idx = firstBrIdx; idx < nodes.length; idx++) {
          if (nodes[idx].isConnected) nodes[idx].remove()
        }

        // Insert a single line break + the remainder after the centered div
        const br = doc.createElement('br')
        div.parentNode?.insertBefore(br, div.nextSibling)
        div.parentNode?.insertBefore(remainderFragment, br.nextSibling)
      })
    }

    return doc.body.innerHTML
  } catch (e) {
    return inputHtml
  }
}

const DEFAULT_FROALA_TOOLBAR_BUTTONS = {
  moreText: {
    buttons: [
      'bold',
      'italic',
      'underline',
      'strikeThrough',
      'subscript',
      'superscript',
      'fontFamily',
      'fontSize',
      'textColor',
      'inlineClass',
      'inlineStyle',
      'clearFormatting',
    ],
  },
  moreParagraph: {
    buttons: [
      'alignLeft',
      'alignCenter',
      'formatOLSimple',
      'alignRight',
      'alignJustify',
      'formatUL',
      'paragraphFormat',
      'paragraphStyle',
      'lineHeight',
      'outdent',
      'indent',
      'quote',
    ],
  },
  moreRich: {
    buttons: ['insertLink', 'insertImage', 'insertVideo', 'insertTable', 'emoticons', 'specialCharacters'],
  },
  moreMisc: {
    buttons: ['undo', 'redo', 'fullscreen', 'print', 'getPDF', 'spellChecker', 'selectAll', 'html', 'help'],
  },
}

const LINK_OPTIONS = {
  paragraphFormat: {
    N: 'Normal',
    H1: 'Heading 1',
    H2: 'Heading 2',
    H3: 'Heading 3',
    H4: 'Heading 4',
  },
  linkEditButtons: ['linkEdit', 'linkRemove'],
  linkList: [
    {
      text: 'Google',
      href: 'http://google.com',
      target: '_blank',
      rel: 'nofollow',
    },
  ],
}

const IMAGE_OPTIONS = {
  imageAllowedTypes: ['jpeg', 'jpg', 'png'],
  imageAltButtons: ['imageBack'],
  imageDefaultAlign: 'left',
  imageDefaultDisplay: 'inline',
  imageDefaultMargin: 8,
  imageDefaultWidth: 200,
  imageEditButtons: ['imageAlign', 'imageCaption', 'imageRemove'],
  imageInsertButtons: ['imageUpload'],
  imageMaxSize: 1024 * 1024 * 2.5,
  imageMinWidth: 32,
  imageMove: false,
  imagePaste: false,
  imageAddNewLine: true,
}

const VIDEO_OPTIONS = {
  videoAllowedProviders: ['youtube'],
  videoAllowedTypes: ['mp4', 'webm', 'm4v', 'mov'],
  videoDefaultAlign: 'left',
  videoDefaultDisplay: 'inline',
  videoEditButtons: ['videoRemove'],
  videoInsertButtons: ['videoUpload'],
  videoMaxSize: SURVEY_QUESTION_VIDEO_MAX_BYTES,
  videoMove: false,
  videoResize: false,
  videoResponsive: true,
}

// custom waveform SVG as a string:
const waveFormSvg = `
  <svg xmlns='http://www.w3.org/2000/svg' width='14' height='12' viewBox='0 0 14 12'>
    <defs>
      <filter id='4np98mrcsa'>
        <feColorMatrix in='SourceGraphic'/>
      </filter>
    </defs>
    <g fill='none' fill-rule='evenodd' filter='url(#4np98mrcsa)' transform='translate(-793 -487)'>
      <path
        fill='#cacaca'
        fill-rule='nonzero'
        d='M800.106 487c.207 0 .374.167.374.374v10.472c0 .207-.167.374-.374.374h-1.122c-.207 0-.374-.167-.374-.374v-10.472c0-.207.167-.374.374-.374h1.122zm-2.805 1.87c.207 0 .374.167.374.374v6.732c0 .207-.167.374-.374.374h-1.122c-.207 0-.374-.167-.374-.374v-6.732c0-.207.167-.374.374-.374h1.122zm8.415 0c.207 0 .374.167.374.374v6.732c0 .207-.167.374-.374.374h-1.122c-.207 0-.374-.167-.374-.374v-6.732c0-.207.167-.374.374-.374h1.122zm-2.805.935c.207 0 .374.167.374.374v4.862c0 .207-.167.374-.374.374h-1.122c-.207 0-.374-.167-.374-.374v-4.862c0-.207.167-.374.374-.374h1.122zm-8.415.935c.207 0 .374.167.374.374v2.992c0 .207-.167.374-.374.374h-1.122c-.207 0-.374-.167-.374-.374v-2.992c0-.207.167-.374.374-.374h1.122z'
      />
    </g>
  </svg>
`

const STATIC_FROALA_CONFIG = {
  key: FROALA_KEY,
  attribution: false,
  listAdvancedTypes: false,
  toolbarSticky: false,
  pasteDeniedAttrs: ['id'],
  ...LINK_OPTIONS,
  ...IMAGE_OPTIONS,
  ...VIDEO_OPTIONS,
}

// Define custom audio button icon and command
FroalaEditor.DefineIconTemplate('waves_svg', waveFormSvg)
FroalaEditor.DefineIcon('customInsertAudio', {
  NAME: '',
  template: 'waves_svg',
})
FroalaEditor.RegisterCommand('customInsertAudio', {
  title: 'Upload Audio',
  focus: false,
  undo: true,
  refreshAfterCallback: true,
  callback: function () {
    if (this.opts.handleAudioUpload) {
      this.opts.handleAudioUpload(this)
    }
  },
})

// Froala's built-in counter treats any non-whitespace token as a word (including
// lonely symbols like +, -, *). Use the same rules as the instrument Word Count.
FroalaEditor.PLUGINS.wordCounter = function (editor) {
  let $counter
  const $ = editor.$

  const getCount = () => countWords(editor.html?.get?.() || editor.el?.innerText || '')

  const update = () => {
    if (!editor.opts.wordCounterCount || !$counter) return
    $counter.text(`${editor.language.translate('Words')} : ${getCount()}`)
    if (editor.opts.toolbarBottom) $counter.css('margin-bottom', editor.$tb.outerHeight(true))
    const scrollbarWidth = editor.$wp.get(0).offsetWidth - editor.$wp.get(0).clientWidth
    if (scrollbarWidth >= 0) {
      if (editor.opts.direction === 'rtl') $counter.css('margin-left', scrollbarWidth)
      else $counter.css('margin-right', scrollbarWidth)
    }
  }

  return {
    _init() {
      if (!editor.$wp || !editor.opts.wordCounterCount) return false
      $counter = $(document.createElement('span')).attr('class', 'fr-counter fr-wCounter')
      $counter.css('bottom', editor.$wp.css('border-bottom-width'))
      if (editor.$second_tb) editor.$second_tb.append($counter)
      else editor.$wp.append($counter)
      editor.events.on('keyup contentChanged input', () => {
        editor.events.trigger('wordCounter.update')
      })
      editor.events.on('wordCounter.update', update)
      editor.events.trigger('wordCounter.update')
      editor.events.on('destroy', () => {
        $counter.removeData().remove()
        $counter = null
      })
    },
    wordCount: getCount,
  }
}

const FroalaTextEditor = forwardRef((props, ref) => {
  const {
    value,
    onChange,
    placeholder,
    readOnly = false,
    hasError = false,
    showToolbar = true,
    charLimit,
    fullFroalaPlugins = false,
    quickInsertEnabled = false,
    moreText = { buttons: [], includeAll: false },
    moreParagraph = { buttons: [], includeAll: false },
    moreRich = { buttons: [], includeAll: false },
    moreMisc = { buttons: [], includeAll: false },
    addItemImage,
    uploadImage,
    addItemMedia,
    deleteItemMedia,
    uploadMedia,
    studyID,
    itemId,
    item,
    className,
    wordCounterCount = true,
  } = props

  const [isFocused, setIsFocused] = useState(false)
  const [videoUploadError, setVideoUploadError] = useState('')

  // Helper: build dynamic toolbar
  const buildToolbar = () => {
    if (!showToolbar) return []
    if (fullFroalaPlugins) return DEFAULT_FROALA_TOOLBAR_BUTTONS
    const groups = { moreText, moreParagraph, moreRich, moreMisc }
    const toolbar = {}
    for (const groupName in groups) {
      const { buttons = [], includeAll = false } = groups[groupName] || {}
      toolbar[groupName] = {
        buttons: includeAll ? DEFAULT_FROALA_TOOLBAR_BUTTONS[groupName].buttons : buttons,
        buttonsVisible: 3,
      }
    }
    return toolbar
  }

  // Inserts image into Froala after upload
  const insertImage = (editor, imageObj) => {
    const { imageId, size, src } = imageObj
    addItemImage?.({ itemId, imageId, imageSrc: src, size })
    editor.image.insert(src, true, { id: imageId }, editor.image.get())
  }

  // Handles Froala's upload
  const onUploadImages = (editor, images) => {
    const image = images[0]
    const formData = new FormData()
    formData.set('study_id', `${studyID}`)
    formData.set('image_name', image.name)
    formData.set('survey_image', image)
    formData.set('alt_text', '')

    uploadImage(formData).then(payload => {
      if (payload) {
        const resPayload = { ...payload, size: image.size }
        insertImage(editor, resPayload)
      }
    })
  }

  // Inserts video into Froala after upload
  const insertVideo = (editor, videoObj) => {
    const { fileId, size, src } = videoObj
    addItemMedia({
      itemId,
      fileId,
      fileSrc: src,
      size,
      isVideo: true,
    })
    editor.html.insert(
      `<video controls style="max-width:100%; border-radius: 8px;" data-id="${fileId}">
          <source src="${src}" type="video/mp4">
      </video>`,
    )
    const html = editor.html.get()
    onChange(html)
  }

  // Inserts audio into Froala after upload
  const insertAudio = useCallback(
    (editor, audioObj) => {
      const { fileId, size, src } = audioObj

      try {
        addItemMedia({
          itemId,
          fileId,
          fileSrc: src,
          size,
        })

        const audioHtml = `
          <div class="audio-wrapper" data-id="${fileId}">
            <audio controls style="width: 100%; max-width: 400px; height: 40px;">
              <source src="${src}" type="audio/mpeg">
            </audio>
          </div>
        `

        editor.html.insert(audioHtml)
        const html = editor.html.get()
        onChange(html)
      } catch (error) {
        console.error('Error inserting audio:', error)
      }
    },
    [addItemMedia, itemId, onChange],
  )

  // Handles Froala's upload for audio
  const onUploadAudio = useCallback(
    (editor, audios) => {
      const audio = audios[0]

      // Validate file type
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3']
      if (!validTypes.includes(audio.type)) {
        console.warn('Invalid audio file type. Please choose .mp3 or .wav.')
        return
      }

      // Validate file size (e.g., 10MB limit)
      const maxSize = 10 * 1024 * 1024
      if (audio.size > maxSize) {
        console.warn('Audio file too large. Maximum size is 10MB.')
        return
      }

      const formData = new FormData()
      formData.set('study_id', `${studyID}`)
      formData.set('file_name', audio.name)
      formData.set('survey_file', audio)

      uploadMedia(formData)
        .then(payload => {
          if (payload) {
            const resPayload = { ...payload, size: audio.size }
            insertAudio(editor, resPayload)
          }
        })
        .catch(error => {
          console.error('Audio upload failed:', error)
        })
    },
    [studyID, uploadMedia, insertAudio],
  )

  // Froala 4 passes native DOM nodes (not jQuery). Support both shapes.
  const getMediaFileId = mediaEl => {
    if (!mediaEl) return null

    if (typeof mediaEl.attr === 'function') {
      const fromAttr = mediaEl.attr('data-id')
      if (fromAttr) return fromAttr
      const nested = mediaEl.find?.('[data-id]')
      if (nested?.attr) return nested.attr('data-id') || null
    }

    const el = mediaEl.jquery || mediaEl.length !== undefined ? mediaEl[0] : mediaEl
    if (!el || typeof el.getAttribute !== 'function') return null

    const directId = el.getAttribute('data-id')
    if (directId) return directId
    const wrapper = el.closest?.('[data-id]')
    if (wrapper) return wrapper.getAttribute('data-id')
    const child = el.querySelector?.('[data-id]')
    return child?.getAttribute('data-id') || null
  }

  const removeAttachedMedia = fileId => {
    if (!itemId || !deleteItemMedia) return
    const resolvedFileId = fileId || item?.resources?.audio || item?.resources?.video
    if (resolvedFileId) {
      deleteItemMedia(itemId, resolvedFileId)
    }
  }

  const onUploadVideo = (editor, videos) => {
    const video = videos[0]
    if (!video) return

    const extension = video.name
      ? `.${video.name.split('.').pop().toLowerCase()}`
      : ''
    const validExtensions = VIDEO_OPTIONS.videoAllowedTypes.map(type => `.${type}`)

    if (!validExtensions.includes(extension)) {
      setVideoUploadError(
        `Please upload a file with the correct extension (${validExtensions.join(', ')})`,
      )
      return
    }

    if (video.size > SURVEY_QUESTION_VIDEO_MAX_BYTES) {
      setVideoUploadError(SURVEY_QUESTION_VIDEO_MAX_SIZE_ERROR)
      return
    }

    setVideoUploadError('')
    const formData = new FormData()
    formData.set('study_id', `${studyID}`)
    formData.set('file_name', video.name)
    formData.set('survey_file', video)

    uploadMedia(formData)
      .then(payload => {
        if (payload) {
          const resPayload = { ...payload, size: video.size }
          insertVideo(editor, resPayload)
          setVideoUploadError('')
        }
      })
      .catch(error => {
        console.error('Video upload failed:', error)
      })
  }

  // Froala config
  const config = useMemo(
    () => ({
      ...STATIC_FROALA_CONFIG,
      placeholderText: placeholder,
      charCounterCount: !!charLimit,
      charCounterMax: charLimit || -1,
      wordCounterCount,
      toolbarButtons: buildToolbar(),
      quickInsertEnabled,
      readOnly,
      handleAudioUpload: editor => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = '.mp3, .wav'
        input.style.display = 'none'
        document.body.appendChild(input)
        input.click()

        input.onchange = () => {
          const files = input.files
          if (files && files.length > 0) {
            const validFiles = Array.from(files).filter(file => ['audio/mpeg', 'audio/wav'].includes(file.type))
            if (validFiles.length > 0) {
              onUploadAudio(editor, validFiles)
            } else {
              console.warn('Invalid audio file type selected. Please choose .mp3 or .wav.')
            }
          }
          document.body.removeChild(input)
        }
      },
      events: {
        blur: function () {
          const html = this.html.get()
          const nextHtml = normalizeFroalaHtml(html)

          const isBlankHtml = maybeHtml => {
            if (typeof maybeHtml !== 'string') return true
            // Strip tags + common nbsp; treat whitespace-only as blank
            const withoutTags = maybeHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
            return withoutTags === ''
          }

          // Froala can sometimes emit effectively-blank HTML on blur even when the
          // controlled `value` is non-empty (e.g., clicking into another input).
          // Don't overwrite non-empty controlled content with that transient blank.
          if (isBlankHtml(nextHtml) && !isBlankHtml(value || '')) return

          onChange?.(nextHtml)
        },
        // 👇 Handle audio deletion
        initialized: function () {
          const editor = this

          // Click handler for deleting audio
          editor.events.on('mouseup', function (e) {
            const target = e.target
            if (target && target.tagName === 'AUDIO') {
              if (window.confirm('Delete this audio?')) {
                const fileId = getMediaFileId(target)
                target.closest('.audio-wrapper')?.remove() || target.remove()
                removeAttachedMedia(fileId)
                editor.undo.saveStep()
                editor.html.sync()
                const html = editor.html.get()
                onChange?.(html)
              }
            }
          })

          editor.events.on('keydown', function (e) {
            const sel = editor.selection.element()
            if (sel && sel.tagName === 'AUDIO' && (e.key === 'Backspace' || e.key === 'Delete')) {
              e.preventDefault()
              const fileId = getMediaFileId(sel)
              sel.closest('.audio-wrapper')?.remove() || sel.remove()
              removeAttachedMedia(fileId)
              editor.undo.saveStep()
              editor.html.sync()
              const html = editor.html.get()
              onChange?.(html)
            }
          })
        },
        'image.beforeUpload': function (images) {
          onUploadImages(this, images)
          return false
        },
        'image.inserted': function (img, response) {
          // Image was inserted in the editor.
        },
        'image.removed': function ($img) {
          // Image was removed in the editor.
        },
        'video.beforeUpload': function (video) {
          onUploadVideo(this, video)
          return false
        },
        'video.removed': function ($video) {
          const fileId = getMediaFileId($video)
          removeAttachedMedia(fileId)
          const html = this.html.get()
          onChange?.(normalizeFroalaHtml(html, { fixCenterLeak: false }))
        },
      },
    }),
    [
      placeholder,
      charLimit,
      wordCounterCount,
      quickInsertEnabled,
      readOnly,
      showToolbar,
      fullFroalaPlugins,
      moreText,
      moreParagraph,
      moreRich,
      moreMisc,
      onChange,
      deleteItemMedia,
      itemId,
      item,
      value,
    ],
  )

  const toolbarKey = useMemo(
    () =>
      JSON.stringify({
        moreText,
        moreParagraph,
        moreRich,
        moreMisc,
        showToolbar,
        fullFroalaPlugins,
      }),
    [moreText, moreParagraph, moreRich, moreMisc, showToolbar, fullFroalaPlugins],
  )

  const showEditorError = hasError || !!videoUploadError

  return (
    <div
      className={`d3-froala-wrapper${className || ''}${showEditorError ? ' error' : ''}${readOnly ? ' read-only' : ''}${isFocused ? ' focused' : ''
        }`}>
      {readOnly ? (
        <FroalaEditorView model={value || ''} />
      ) : (
        <FroalaEditorComponent
          key={toolbarKey}
          tag='textarea'
          model={value || ''}
          onModelChange={html => onChange?.(normalizeFroalaHtml(html, { fixCenterLeak: false }))}
          config={config}
        />
      )}
      {videoUploadError ? <p className='error froala-media-upload-error'>{videoUploadError}</p> : null}
    </div>
  )
})

export default FroalaTextEditor
